import React, {useEffect, useState, useMemo} from "react";
import styled from "styled-components";
import Rating from "../../common/components/modules/Rating";
import {PrimaryLoadingButton} from "../../common/components/elements/button/PrimaryButton";
import {FormContainer} from "../../common/components/collections/form/forms";
import {Form} from "react-final-form";
import OrderTipField from "./OrderTipField";
import {makePayment} from "../../payments/factory";
import * as Notifications from "src/core/notifications";
import useUserProfile from "src/core/authentication/hooks/useUserProfile";
import {TitleLabel} from "../../checkout/components/components";
import {formatPrice} from "src/core/common/price";
import useRouter from "../../common/hooks/useRouter";
import useOrderState from "src/core/checkout/hooks/useOrderState";
import usePaymentOptions from "src/core/checkout/hooks/usePaymentOptions";

function OrderTipForm({order, rating, onSuccess, submitText, readOnly}) {
  const paymentOptionCode = order.getPaymentSpecification().getPaymentOption();
  const paymentService = useMemo(
    () => makePayment(paymentOptionCode),
    [paymentOptionCode]
  );
  const [loading, setLoading] = useState(false);
  const [, authApi] = useUserProfile();
  const user = authApi.userProfile();
  const router = useRouter();
  const [, ECommerceAction] = useOrderState();

  useEffect(() => {
    return paymentService.subscribe(({event, data}) => {
      if (event === "tip-success") {
        setLoading(false);
        Notifications.success("Thank you for your tip.");
        // refresh order
        ECommerceAction.loadOrder(order.getId());
        onSuccess();
      } else if (event === "tip-failure") {
        setLoading(false);
        Notifications.error("Failed to add tip.");
      }
    });
  }, [ECommerceAction, onSuccess, order, paymentService]);

  async function onSubmit(formValues) {
    if (!user || !order) return;

    setLoading(true);
    await paymentService.addTip({
      ...formValues.tip,
      paymentSpecification: order.getPaymentSpecification(),
      user,
    });
  }

  const tipValue = order.getTipValue();

  const [paymentOptions] = usePaymentOptions();
  let paymentOption;
  if (Array.isArray(paymentOptions)) {
    paymentOption = paymentOptions.find(opt => opt.getType() === paymentOptionCode);
  }
  if (paymentOption && !paymentOption.supportsTipping()) {
    Notifications.warn("Order does not support tipping.");
    router.safeBack();
  }

  return (
    <>
      {readOnly && tipValue ? (
        <Tip>
          <TitleLabel>Tip:</TitleLabel>
          <TipValue>{formatPrice(tipValue)}</TipValue>
        </Tip>
      ) : (
        <Form
          onSubmit={onSubmit}
          render={({handleSubmit}) => (
            <form onSubmit={handleSubmit}>
              <CenteredFormContainer>
                {rating && <Rating initialRating={rating} readonly />}
                <OrderTipField order={order} />
                <PrimaryLoadingButton
                  type="submit"
                  data-cy="submitTipButton"
                  loading={loading}
                  label={submitText}
                />
              </CenteredFormContainer>
            </form>
          )}
        />
      )}
    </>
  );
}

const CenteredFormContainer = styled(FormContainer)`
  display: flex;
  flex-flow: column;
  align-items: center;
  > *:not(:last-child) {
    margin-bottom: ${({theme}) => theme.v2.spacing(8)};
  }
`;
const Tip = styled.div`
  display: flex;
  justify-content: space-between;
`;
const TipValue = styled.div`
  align-self: flex-end;
`;

export default OrderTipForm;
